import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '@app/services/api';
import '@platform/ui/SearchableSelect/SearchableSelect.css';
import { placeDropdown } from './placement.mjs';

// allowNone: whether the list offers a "None" row that clears the value.
// Defaults to true, which is what every caller before this expected. A
// REQUIRED field passes false — otherwise the picker hands the user a way
// to empty a field the form will then refuse to save.
function SearchableSelect({ options, value, onChange, disabled, placeholder, iconUrl, noneLabel, allowNone = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  // The portalled dropdown (see render below). We track a ref so the
  // click-outside handler can allow clicks inside the dropdown — once
  // portalled to document.body it's no longer a descendant of wrapperRef.
  const dropdownRef = useRef(null);
  // Dropdown anchor coords. `position: fixed` so the dropdown escapes
  // any overflow:hidden / overflow:auto ancestor (modal, table-wrap,
  // form scroller) AND — via createPortal(document.body) — any
  // `transform`-ed ancestor that would otherwise pin `position:fixed`
  // to the ancestor instead of the viewport (react-grid-layout tiles
  // are the current biggest offender: they use translate3d).
  const [anchor, setAnchor] = useState(null);

  const none = noneLabel || 'None';
  // The none row occupies index 0 when it is offered, so every other index
  // shifts by one. Keeping that in one constant keeps the keyboard
  // arithmetic and the rendered order from drifting apart.
  const offset = allowNone ? 1 : 0;

  // Find selected option label
  const selectedOption = (options || []).find(o => {
    const oId = o.id || o.accountId || o.value;
    return String(oId) === String(value);
  });
  const displayLabel = selectedOption
    ? (selectedOption.displayName || selectedOption.name || selectedOption.value || selectedOption.label)
    : (allowNone ? none : (placeholder || 'Select…'));

  const filtered = (options || []).filter(o => {
    const label = (o.displayName || o.name || o.value || o.label || '').toLowerCase();
    return label.includes(search.toLowerCase());
  });

  // Total items: the none option (index 0) when offered, then filtered.
  const totalItems = filtered.length + offset;

  // Focus search when opened. Defer to the next animation frame so the
  // dropdown has actually been painted — on first open the element is
  // briefly visibility:hidden (anchor is still null), and focus() is a
  // no-op on a hidden element. Without the rAF, users had to click the
  // search input a second time to start typing.
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => {
      if (searchRef.current) searchRef.current.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // Reset search and highlight when opened/closed
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Reset highlight on search change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Close on outside click. Dropdown is portalled to <body> so a check
  // against wrapperRef alone would treat every click inside the dropdown
  // as "outside" and close it — extend to accept clicks on the dropdown
  // subtree too.
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Compute trigger rect on open + re-anchor on scroll / resize. Uses
  // capture-phase scroll so we catch scroll on any ancestor.
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Flip upward when the list would run off the bottom of the viewport
      // (placement.js). The dropdown is already in the DOM (hidden) on the
      // first pass, so its real height is measurable; 260 is the CSS max
      // (search row + 200px list) as a fallback.
      const dropdownHeight = dropdownRef.current?.offsetHeight || 260;
      setAnchor(placeDropdown({ rect, dropdownHeight, viewportHeight: window.innerHeight }));
    };
    update();
    let rafId = 0;
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = 0; update(); });
    };
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [isOpen]);

  // Scroll highlighted into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.ss-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleSelect = useCallback((val) => {
    onChange(val);
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allowNone && highlightedIndex === 0) {
        handleSelect('');
      } else if (highlightedIndex >= offset && highlightedIndex < totalItems) {
        const opt = filtered[highlightedIndex - offset];
        handleSelect(opt.id || opt.accountId || opt.value);
      }
    }
  }, [totalItems, highlightedIndex, filtered, handleSelect, allowNone, offset]);

  return (
    <div className="ss-wrapper" ref={wrapperRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`ss-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {iconUrl && (
          <img src={api.getJiraImageUrl(iconUrl)} alt="" className="ss-trigger-icon" />
        )}
        <span className="ss-trigger-label">{displayLabel}</span>
        <svg className="ss-trigger-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="ss-dropdown"
          style={anchor
            ? { position: 'fixed', left: anchor.left, width: anchor.width, right: 'auto',
                top: anchor.top ?? 'auto', bottom: anchor.bottom ?? 'auto' }
            : { visibility: 'hidden' }}
        >
          <input
            ref={searchRef}
            type="text"
            className="ss-search"
            placeholder={placeholder || 'Search...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="ss-options" ref={listRef}>
            {allowNone && (
              <div
                className={`ss-option ${!value ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
                onClick={() => handleSelect('')}
                onMouseEnter={() => setHighlightedIndex(0)}
              >
                <span className="ss-option-label" style={{ fontStyle: !value ? 'normal' : 'italic', opacity: 0.7 }}>
                  {none}
                </span>
              </div>
            )}
            {filtered.map((opt, idx) => {
              const optId = opt.id || opt.accountId || opt.value;
              const label = opt.displayName || opt.name || opt.value || opt.label;
              const isSelected = String(optId) === String(value);
              const itemIndex = idx + offset;
              return (
                <div
                  key={optId}
                  className={`ss-option ${isSelected ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(optId)}
                  onMouseEnter={() => setHighlightedIndex(itemIndex)}
                >
                  {opt.iconUrl && (
                    <img src={api.getJiraImageUrl(opt.iconUrl)} alt="" className="ss-option-icon" />
                  )}
                  {opt.avatarUrls?.['24x24'] && (
                    <img src={api.getJiraImageUrl(opt.avatarUrls['24x24'])} alt="" className="ss-option-avatar" />
                  )}
                  <span className="ss-option-label">{label}</span>
                  {isSelected && <span className="ss-option-check">✓</span>}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div className="ss-empty">No matches found</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default SearchableSelect;
